import React, { createContext, ReactNode, useContext } from 'react';
import api from '../services/api';
import { Return, ReturnStatus } from '../models/Return';
import { ReturnReason } from '../models/ReturnItem';

interface ReturnItemCreationPayload {
  orderItemId: string;
  quantity: number;
  reason: ReturnReason;
  description?: string;
}

interface CreateReturnPayload {
  orderId: string;
  items: ReturnItemCreationPayload[];
  comments?: string;
}

interface ReturnContextType {
  createReturn: (data: CreateReturnPayload) => Promise<{
    returnId: string;
    returnNumber: string;
  }>;
  getReturn: (id: string) => Promise<Return>;
  getReturnsByOrder: (orderId: string) => Promise<Return[]>;
  getAllReturns: (
    page?: number,
    limit?: number,
    status?: ReturnStatus,
    search?: string,
  ) => Promise<{
    returns: Return[];
    total: number;
    page: number;
    totalPages: number;
  }>;
  changeReturnStatus: (id: string, status: ReturnStatus) => Promise<{
    returnId: string;
    status: ReturnStatus;
  }>;
  processRefund: (id: string) => Promise<{
    returnId: string;
    refundAmount: number;
    refundId: string;
  }>;
  getUserReturns: (
    page?: number,
    limit?: number,
    status?: ReturnStatus,
  ) => Promise<{
    returns: Return[];
    total: number;
    page: number;
    totalPages: number;
  }>;
}

const ReturnContext = createContext<ReturnContextType | undefined>(undefined);

export const useReturn = () => {
  const context = useContext(ReturnContext);
  if (!context) {
    throw new Error('useReturn must be used within a ReturnProvider');
  }
  return context;
};

interface ReturnProviderProps {
  children: ReactNode;
}

export const ReturnProvider: React.FC<ReturnProviderProps> = ({ children }) => {
  const reactAppApiUrl = process.env.REACT_APP_API_URL;
  if (!reactAppApiUrl) {
    throw new Error('REACT_APP_API_URL is not set');
  }

  const apiUrl = new URL('returns/', reactAppApiUrl).toString();

  // Tworzenie nowego zwrotu
  const createReturn = async (
    data: CreateReturnPayload,
  ): Promise<{ returnId: string; returnNumber: string }> => {
    try {
      const response = await api.post(new URL('create', apiUrl).toString(), data);
      return {
        returnId: response.data.returnId,
        returnNumber: response.data.returnNumber,
      };
    } catch (error: any) {
      console.error('Failed to create return', error);
      throw new Error(error.response?.data?.message || 'Server error');
    }
  };

  // Pobieranie zwrotu po ID
  const getReturn = async (id: string): Promise<Return> => {
    try {
      const response = await api.get(new URL(`${id}`, apiUrl).toString());
      return response.data;
    } catch (error: any) {
      console.error('Failed to fetch return', error);
      throw new Error(error.response?.data?.message || 'Server error');
    }
  };

  // Pobieranie zwrotów dla zamówienia
  const getReturnsByOrder = async (orderId: string): Promise<Return[]> => {
    try {
      const response = await api.get(new URL(`order/${orderId}`, apiUrl).toString());
      return response.data;
    } catch (error: any) {
      console.error('Failed to fetch returns by order', error);
      throw new Error(error.response?.data?.message || 'Server error');
    }
  };

  // Pobieranie wszystkich zwrotów (tylko dla admina)
  const getAllReturns = async (
    page = 1,
    limit = 10,
    status?: ReturnStatus,
    search?: string,
  ): Promise<{
    returns: Return[];
    total: number;
    page: number;
    totalPages: number;
  }> => {
    try {
      const params: Record<string, string | number | undefined> = {
        page,
        limit,
        search,
      };

      if (status) {
        params.status = status;
      }

      const response = await api.get(new URL('admin', apiUrl).toString(), {
        params,
      });
      return response.data;
    } catch (error: any) {
      console.error('Failed to fetch all returns', error);
      throw new Error(error.response?.data?.message || 'Server error');
    }
  };

  // Zmiana statusu zwrotu (tylko dla adminów)
  const changeReturnStatus = async (
    id: string,
    status: ReturnStatus,
  ): Promise<{
    returnId: string;
    status: ReturnStatus;
  }> => {
    try {
      const response = await api.patch(new URL(`${id}/status`, apiUrl).toString(), {
        status,
      });
      return {
        returnId: response.data.returnId,
        status: response.data.status,
      };
    } catch (error: any) {
      console.error('Failed to change return status', error);
      throw new Error(error.response?.data?.message || 'Server error');
    }
  };

  // Przetwarzanie zwrotu pieniędzy (tylko dla adminów)
  const processRefund = async (
    id: string,
  ): Promise<{
    returnId: string;
    refundAmount: number;
    refundId: string;
  }> => {
    try {
      const response = await api.post(new URL(`${id}/refund`, apiUrl).toString());
      return {
        returnId: response.data.returnId,
        refundAmount: response.data.refundAmount,
        refundId: response.data.refundId,
      };
    } catch (error: any) {
      console.error('Failed to process refund', error);
      throw new Error(error.response?.data?.message || 'Server error');
    }
  };

  // Pobieranie zwrotów zalogowanego użytkownika
  const getUserReturns = async (
    page = 1,
    limit = 10,
    status?: ReturnStatus,
  ): Promise<{
    returns: Return[];
    total: number;
    page: number;
    totalPages: number;
  }> => {
    try {
      const params: Record<string, string | number | undefined> = {
        page,
        limit,
      };

      if (status) {
        params.status = status;
      }

      const response = await api.get(new URL('my-returns', apiUrl).toString(), {
        params,
      });
      return response.data;
    } catch (error: any) {
      console.error('Failed to fetch user returns', error);
      throw new Error(error.response?.data?.message || 'Server error');
    }
  };

  return (
    <ReturnContext.Provider
      value={{
        createReturn,
        getReturn,
        getReturnsByOrder,
        getAllReturns,
        changeReturnStatus,
        processRefund,
        getUserReturns,
      }}
    >
      {children}
    </ReturnContext.Provider>
  );
};
